import { useWorkspaceText } from '../interface-copy';
import { useRef, useState, type FormEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { customMcpApi } from './api';
import { errorMessage } from './presentation';
import { useCompany } from '@/company/context';
import { CompanyRequestError } from '@/company/api';
import type { ConnectionAccessChoice } from '../connection-read-access/api';
import { ConnectionPolicyFields, useConnectionPeople } from '../connect-access/policy-fields';
import { useCurrentConnectionRequest } from '../connect-access/use-current-request';
import { useNamespaceCopy, formatCopy } from '@/i18n/ns';
import accessCopyCatalog from '@/i18n/ns/connection-access';
import {
  shopifyFieldErrors,
  shopifyErrorField,
  shopifyFieldLabels,
  type ShopifyFieldErrors,
} from './shopify-validation';

const DEV_DASHBOARD = 'https://dev.shopify.com/dashboard';

/** Connect a store through the company's own Shopify Developer App, on the common connect sheet. */
export function ShopifyDevAppDialog({
  companyName,
  canConnectCompany,
  initialAudience = 'private',
  onClose,
  onCreated,
}: {
  companyName: string;
  canConnectCompany: boolean;
  initialAudience?: 'private' | 'company';
  onClose: () => void;
  onCreated: (
    response: Awaited<ReturnType<typeof customMcpApi.createShopifyDevApp>>,
    /** The connect-time reading choice, applied only to this new connection's own grant. */
    choice: ConnectionAccessChoice,
  ) => void;
}) {
  const { text: uiText, rich: uiRich, locale } = useWorkspaceText();
  const company = useCompany().data;
  const request = useCurrentConnectionRequest();
  const copy = useNamespaceCopy(accessCopyCatalog);
  const [fields, setFields] = useState<ShopifyFieldErrors>({});
  const people = useConnectionPeople();
  const [choice, setChoice] = useState<ConnectionAccessChoice>({
    audience: initialAudience === 'company' && canConnectCompany ? 'company' : 'private',
    people: [],
    changes: 'ask',
  });
  const [shop, setShop] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const storeInput = useRef<HTMLInputElement>(null);
  const form = useRef<HTMLFormElement>(null);
  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    const problems = shopifyFieldErrors({ shop, clientId, clientSecret }, (kind, field) =>
      formatCopy(copy[kind], { field: shopifyFieldLabels[field] }),
    );
    setFields(problems);
    const first = Object.keys(problems)[0];
    if (first) {
      setError(uiText('Add the store, the client ID and the client secret.'));
      form.current?.querySelector<HTMLInputElement>(`input[name="${first}"]`)?.focus();
      return;
    }
    const owner = request.begin();
    if (!owner) return;
    setBusy(true);
    try {
      const response = await customMcpApi.createShopifyDevApp(
        {
          shop: shop.trim(),
          clientId: clientId.trim(),
          clientSecret,
          audience: choice.audience === 'company' ? 'company' : 'private',
        },
        owner.fetch,
      );
      if (!owner.current()) return;
      setClientSecret('');
      onCreated(response, choice);
    } catch (requestError) {
      if (!owner.current()) return;
      const field = shopifyErrorField(requestError);
      const message = errorMessage(requestError, locale);
      if (field) {
        setFields({
          [field]:
            requestError instanceof CompanyRequestError &&
            requestError.code === 'shopify_shop_invalid'
              ? message
              : formatCopy(copy.fieldInvalid, { field: shopifyFieldLabels[field] }),
        });
        form.current?.querySelector<HTMLInputElement>(`input[name="${field}"]`)?.focus();
      } else setError(message);
    } finally {
      if (owner.finish()) setBusy(false);
    }
  }
  return (
    <Dialog open onOpenChange={(open) => !open && !busy && onClose()}>
      <DialogContent
        className="pc-portal ws-access-sheet ws-shopify-sheet"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          storeInput.current?.focus();
        }}
      >
        <header>
          <DialogTitle>{uiText('Connect your Shopify Developer App')}</DialogTitle>
          <DialogDescription>
            {uiText(
              'Poneva can use exactly the access your store gave the app, and nothing more. It talks to Shopify directly and keeps the client secret encrypted.',
            )}
          </DialogDescription>
        </header>
        <form ref={form} className="ws-shopify-form" onSubmit={submit}>
          <div className="ws-access-sheet-body">
            <details className="ws-shopify-steps">
              <summary>{uiText('How to create the app in Shopify')}</summary>
              <ol>
                <li>
                  {uiRich(
                    'Open{value1} {value2}{value3} with the account that owns the store, and create an app.',
                    {
                      value1: ' ',
                      value2: (
                        <a href={DEV_DASHBOARD} target="_blank" rel="noreferrer">
                          {uiText('Shopify’s Dev Dashboard')}
                        </a>
                      ),
                      value3: ' ',
                    },
                  )}
                </li>
                <li>
                  {uiText(
                    'Create a version and select the access scopes Poneva may use, for example read_products and read_orders. Add write_ scopes only for data Poneva may change. Then release the version.',
                  )}
                </li>
                <li>
                  {uiText(
                    'For orders and customers, also select protected customer data in the app’s settings. Shopify allows this without review for an app made for your own store.',
                  )}
                </li>
                <li>{uiText('Install the app on your store.')}</li>
                <li>{uiText('Copy the client ID and client secret from the app’s settings.')}</li>
              </ol>
              <p>
                {uiText(
                  'To change the access later, release a new version, approve it on the store and use Test connection here.',
                )}
              </p>
            </details>
            <div className="ws-shopify-credentials">
              <label>
                {uiText('Store')}
                <input
                  ref={storeInput}
                  name="shop"
                  aria-invalid={fields.shop ? 'true' : undefined}
                  aria-describedby={fields.shop ? 'shopify-shop-error' : undefined}
                  value={shop}
                  autoComplete="off"
                  onChange={(event) => setShop(event.target.value)}
                  onInput={(event) => setShop(event.currentTarget.value)}
                  placeholder={uiText('your-store.myshopify.com')}
                />
                <small>
                  {uiText(
                    'Your store’s .myshopify.com address (Shopify admin → Settings → Domains), or paste the address of your Shopify admin page',
                  )}
                </small>
                {fields.shop && (
                  <small id="shopify-shop-error" className="ws-custom-mcp-form-error" role="alert">
                    {fields.shop}
                  </small>
                )}
              </label>
              <div className="ws-shopify-credential-pair">
                <label>
                  {uiRich('Client ID {value1} {value2}', {
                    value1: (
                      <input
                        name="clientId"
                        aria-invalid={fields.clientId ? 'true' : undefined}
                        aria-describedby={fields.clientId ? 'shopify-clientId-error' : undefined}
                        value={clientId}
                        autoComplete="off"
                        onChange={(event) => setClientId(event.target.value)}
                        onInput={(event) => setClientId(event.currentTarget.value)}
                      />
                    ),
                    value2: fields.clientId && (
                      <small
                        id="shopify-clientId-error"
                        className="ws-custom-mcp-form-error"
                        role="alert"
                      >
                        {fields.clientId}
                      </small>
                    ),
                  })}
                </label>
                <label>
                  {uiRich('Client secret {value1} {value2}', {
                    value1: (
                      <input
                        name="clientSecret"
                        aria-invalid={fields.clientSecret ? 'true' : undefined}
                        aria-describedby={
                          fields.clientSecret ? 'shopify-clientSecret-error' : undefined
                        }
                        type="password"
                        autoComplete="new-password"
                        value={clientSecret}
                        onChange={(event) => setClientSecret(event.target.value)}
                        onInput={(event) => setClientSecret(event.currentTarget.value)}
                      />
                    ),
                    value2: fields.clientSecret && (
                      <small
                        id="shopify-clientSecret-error"
                        className="ws-custom-mcp-form-error"
                        role="alert"
                      >
                        {fields.clientSecret}
                      </small>
                    ),
                  })}
                </label>
              </div>
            </div>
            <ConnectionPolicyFields
              value={choice}
              onChange={setChoice}
              companyName={companyName}
              canCompany={canConnectCompany}
              ownerUserId={company?.membership?.userId ?? null}
              people={people}
              disabled={busy}
            />
            {error && (
              <p className="ws-shopify-error" role="alert">
                {error}
              </p>
            )}
          </div>
          <footer>
            <Button type="button" variant="outline" disabled={busy} onClick={onClose}>
              {uiText('Cancel')}
            </Button>
            <Button
              type="submit"
              disabled={busy || (choice.audience === 'people' && !choice.people.length)}
            >
              {busy ? uiText('Connecting…') : uiText('Test and connect')}
            </Button>
          </footer>
        </form>
      </DialogContent>
    </Dialog>
  );
}
